import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useGetSalesOrderQuery, usePrintInvoiceNetworkMutation } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { shareSalesOrderPdf, exportSalesOrderToPdf } from '../utils/exportSalesOrderPdf';
import qzService from '../../../shared/utils/qzService';
import { SalesInvoiceTemplateRenderer } from '../components/SalesInvoiceTemplateRenderer';

function SalesOrderPrint() {
  const { id } = useParams();
  const { data: order, isLoading: loadingOrder, error } = useGetSalesOrderQuery(id);
  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();
  const isLoading = loadingOrder || loadingCompany;
  const [printNetwork, { isLoading: isPrinting }] = usePrintInvoiceNetworkMutation();

  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const paperType = company?.printPaperType || (isAndroid ? 'letter' : 'a4');
  const salesTemplate = company?.salesPrintTemplate || 'standard';

  const currency = company?.currency || 'USD';
  const itemFormat = company?.printItemNameFormat || 'name_sku';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const [showTax, setShowTax] = useState(true);
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);
  const [isQzPrinting, setIsQzPrinting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const templateKey = [
    'standard',
    'thermal',
    'template3',
    'template4',
    'template5',
    'template6',
    'template7',
    'template8',
    'template9',
    'template10',
    'template11',
    'template12',
    'a5',
  ].includes(salesTemplate)
    ? salesTemplate
    : 'standard';

  const isA5 = templateKey === 'a5' || paperType === 'a5';
  const isThermal = templateKey === 'thermal' || paperType === 'thermal';
  const pageWidth = isThermal ? '80mm' : (isA5 ? '148mm' : (paperType === 'letter' ? '215.9mm' : '210mm'));

  const handleQzPrint = async () => {
    if (!company?.qzPrinterName) {
      alert('No QZ Printer Name configured. Please select a printer in Settings.');
      return;
    }
    setIsQzPrinting(true);
    try {
      const pdfArrayBuffer = await exportSalesOrderToPdf(order, company, {
        templateKey,
        paperType,
        currency,
        itemFormat,
        dateFormat,
        showTax,
        element: document.querySelector('.print-content'),
        filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
        save: false,
      });

      const base64Pdf = btoa(
        new Uint8Array(pdfArrayBuffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      await qzService.printPdf(company.qzPrinterName, base64Pdf, {
        jobName: `Invoice-${order.invoiceNumber || order.orderNumber}`,
      });
    } catch (err) {
      alert(`QZ Print failed: ${err.message}`);
    } finally {
      setIsQzPrinting(false);
    }
  };

  useEffect(() => {
    if (!isLoading && order && company?.enableAutoPrintOnSave && !hasAutoPrinted) {
      setHasAutoPrinted(true);
      if (company?.enableQzPrint && company?.qzPrinterName) {
        handleQzPrint();
      } else {
        setShowTax(true);
        setTimeout(() => {
          window.focus();
          window.print();
        }, 500);
      }
    }
  }, [order?.id, company?.id, isLoading, hasAutoPrinted]);

  const handleShare = async () => {
    if (!order) return;
    setIsSharing(true);
    try {
      await shareSalesOrderPdf(order, company, {
        templateKey,
        paperType,
        currency,
        itemFormat,
        dateFormat,
        showTax,
        element: document.querySelector('.print-content'),
        filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleDirectPrint = async (silent = false) => {
    try {
      await printNetwork(id).unwrap();
      if (!silent) alert('Sent to network printer successfully');
    } catch (err) {
      const msg = err.data?.message || err.message;
      if (!silent) alert(`Print failed: ${msg}`);
      else console.error('Silent print failed:', msg);
    }
  };

  useEffect(() => {
    if (order && company?.enableNetworkThermalPrinter && company?.enableDirectPrintSilently && !isLoading) {
      handleDirectPrint(true);
    }
  }, [order?.id, company?.id, isLoading]);

  if (isLoading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  if (error || !order) return <Typography color="error" sx={{ p: 3 }}>Error loading sales order</Typography>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <style>{`
        @page { 
          size: ${isThermal ? '80mm auto' : (isA5 ? 'A5' : (paperType === 'letter' ? 'letter' : 'A4'))}; 
          margin: 0;
        }
        @media print {
          body { margin: 0; padding: 0; background: #fff !important; }
          .no-print { display: none !important; }
          .print-content {
            width: 100% !important;
            max-width: ${pageWidth} !important;
            margin: 0 auto !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            box-sizing: border-box !important;
            background: #fff !important;
          }
        }
      `}</style>

      <Box className="no-print" sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => {
            setShowTax(true);
            setTimeout(() => window.print(), 100);
          }}
          sx={{ borderRadius: 2 }}
        >
          Print (with Tax)
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            setShowTax(false);
            setTimeout(() => window.print(), 100);
          }}
          sx={{ borderRadius: 2 }}
        >
          Print (No Tax)
        </Button>
        <Button
          variant="outlined"
          color="info"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleShare}
          disabled={isSharing}
          sx={{ borderRadius: 2 }}
        >
          {isSharing ? 'Preparing...' : 'Share'}
        </Button>
        {company?.enableQzPrint && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PrintIcon />}
            onClick={handleQzPrint}
            disabled={isQzPrinting}
            sx={{ borderRadius: 2 }}
          >
            {isQzPrinting ? 'Printing...' : `Direct Print (QZ: ${company.qzPrinterName || 'Default'})`}
          </Button>
        )}
        {company?.enableNetworkThermalPrinter && (
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<PrintIcon />} 
            onClick={() => handleDirectPrint(false)}
            disabled={isPrinting}
            sx={{ borderRadius: 2 }}
          >
            {isPrinting ? 'Printing...' : 'Direct Print (Network)'}
          </Button>
        )}
      </Box>

      <Box
        className="print-content"
        sx={{
          width: pageWidth,
          maxWidth: '100%',
          m: '0 auto',
          boxSizing: 'border-box',
          bgcolor: '#fff',
          boxShadow: { xs: 'none', sm: '0 4px 14px rgba(0,0,0,0.08)' },
          borderRadius: { xs: 0, sm: 1.5 },
          overflow: 'hidden',
        }}
      >
        <SalesInvoiceTemplateRenderer
          order={order}
          company={company}
          templateKey={templateKey}
          currency={currency}
          itemFormat={itemFormat}
          dateFormat={dateFormat}
          showTax={showTax}
        />
      </Box>
    </Box>
  );
}

export default SalesOrderPrint;
